import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { m } from 'framer-motion';
import classNames from 'classnames';
import { useFormContext } from '@/context/FormContext';
import generateValidationSchema from '@/helpers/validation/schema';
import { Questionnaire } from '@/typings/Questionnaire';
import Button from '@/components/DesignLibrary/Button';
import { useIsMobile } from '@/hooks';
import ProgressIndicator from '@components/ProgressIndicator';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import TabText from '@components/DesignLibrary/Atoms/TabText';
import removeNullish from '@helpers/removeNullish';
import { useFeatureFlag } from '@providers/FeatureFlags';
import Spacer from '@components/Spacer';
import CheckCircle from '@components/Icons/CheckCircle';
import ToggleLeft from '@components/Icons/ToggleLeft';
import ToggleRight from '@components/Icons/ToggleRight';
import { useOpenDataReferencePane } from '@panes/dataReferenceHooks';
import FormPDFViewer from '@components/Questionnaire/form/FormPDFViewer';
import { DocumentAccessProvider } from '@providers/DocumentAccess';
import JsonModal from '@modals/JsonModal';
import trackEvents from '@/helpers/trackEvents';
import FormField from '../FormField';
import SectionNavigation from '../SectionNavigation';
import SectionNavigationButton from '../SectionNavigationButton';
import AutofillExplanationModal from './AutofillExplanationModal';
import ExternalData from './ExternalData';
import HandleWatchers from '../HandleWatchers';
import SaveButton from './SaveButton';
import SubmitButton from './SubmitButton';
import s from './DynamicForm.module.scss';
import useFilteredSections from './useFilteredSections';

type Props = {
  formData: Questionnaire.Questionnaire;
  isEnabled: boolean;
  isCompleted: boolean;
  getFieldTemplatesForSectionTemplate: (section: Pick<QuestionnaireApiModels.SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>) => Promise<void>;
  shouldForceComplete?: boolean;
};

const FormContent: React.FC<Props> = ({ getFieldTemplatesForSectionTemplate, formData, isEnabled, isCompleted, shouldForceComplete }) => {
  const {
    formMethods,
    fieldsByKey,
    currentSection,
    setValue,
    setCurrentSection,
    userWorkflowId,
  } = useFormContext();
  trackEvents.track('render - FormContent');
  const { control } = formMethods;
  const [selectedAssignee, setSelectedAssignee] = useState('All');
  const [currentSubsection, setCurrentSubsection] = useState<string | null>(null);
  const [showFillablePdf, setShowFillablePdf] = useState(false);
  const [showExternalData, setShowExternalData] = useState(false);
  const [pdfDocumentId, setPdfDocumentId] = useState<string | null>(null);
  const [pdfJson, setPdfJson] = useState<Record<string, unknown> | null>(null);
  const [pdfError, setPdfError] = useState<Record<string, unknown> | null>(null);
  const [isPdfJsonModalOpen, setIsPdfJsonModalOpen] = useState(false);
  const [isPdfErrorModalOpen, setIsPdfErrorModalOpen] = useState(false);
  const [isFetchingPdfDocument, setIsFetchingPdfDocument] = useState(false);
  const [fieldsAutofilling, setFieldsAutoFilling] = useState<string[]>([]);
  const [explanationModal,setExplanationModal] = useState<boolean>(false);
  const [fieldDataForExplanation,setFieldDataForExplanation] = useState<Questionnaire.Field>();
  const setDataReferenceId = useOpenDataReferencePane();
  const isMobile = useIsMobile();
  const { getData: generateFormPdfDocument } = useNoodleApi(tsClient.workflows.generateFormPDF);
  const { getData: getFormPdfDocumentId } = useNoodleApi(tsClient.workflows.getFormPDFDocumentId);

  const canUsePdfFillInsights = useFeatureFlag('pdf-fill-insights');
  const canShowExternalData = useFeatureFlag('questionnaire-show-external-data');

  const handleToggleShowFillablePdf = useCallback(() => setShowFillablePdf(prev => !prev), []);
  const handleToggleShowExternalData = useCallback(() => setShowExternalData(prev => !prev), []);

  useEffect(() => {
    if (currentSection) {
      window.scrollTo({
        top: 0,
      });
    }
  }, [currentSection]);

  const assignees = useMemo(() => formData.assigneeTemplates, [formData.assigneeTemplates]);

  const { currentSectionData, visibleSections, sectionsForNavigation } = useFilteredSections({
    allSections: formData.sections,
    selectedAssignee,
  });

  useEffect(() => {
    const getData = async (): Promise<void> => {
      if (currentSectionData?.pdfTemplateId && userWorkflowId) {
        const { data } = await getFormPdfDocumentId({
          pdfTemplateId: currentSectionData.pdfTemplateId,
          questionnaireId: formData.id,
          userWorkflowId,
        });
        if (data?.documentId) {
          setPdfDocumentId(data.documentId);
        }
      }
    };
    getData();
  }, [currentSectionData?.pdfTemplateId, userWorkflowId, formData.id, getFormPdfDocumentId]);

  useEffect(() => {
    setPdfJson(null);
    setPdfError(null);
  }, [currentSection]);

  useEffect(() => {
    if (currentSectionData && currentSectionData.isLoading) {
      getFieldTemplatesForSectionTemplate(currentSectionData);
    }
  }, [getFieldTemplatesForSectionTemplate, currentSectionData]);

  useEffect(() => {
    setCurrentSubsection(currentSectionData?.subsections?.[0] || null);
  }, [currentSectionData?.subsections]);

  const isLastSection = visibleSections.length > 0
    ? currentSection === visibleSections[visibleSections.length - 1].id
    : false;

  const isLastSubsection = currentSubsection !== null && currentSectionData?.subsections
    ? currentSubsection === currentSectionData.subsections[currentSectionData.subsections.length - 1]
    : false;

  const nextSectionIndex = visibleSections.findIndex(({ id }) => id === currentSection) + 1;

  const handleNextSection = (): void => {
    const currentSubsections = currentSectionData?.subsections || [];
    const currentSubsectionIndex = currentSubsections.findIndex(subsection => subsection === currentSubsection);

    const panes = document.getElementsByClassName('pane-content');
    const lastPaneElement = panes[panes.length - 1];
    if (lastPaneElement) {
      lastPaneElement.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }

    if (currentSubsectionIndex < currentSubsections.length - 1) {
      setCurrentSubsection(currentSubsections[currentSubsectionIndex + 1]);
    } else if (visibleSections[nextSectionIndex]) {
      setCurrentSection(visibleSections[nextSectionIndex].id);
    }
  };

  const validationSchema = useMemo(() =>
    generateValidationSchema(
      formData.sections.flatMap(section => section.fields),
    ), [formData.sections],
  );

  useEffect(() => {
    Object.values(fieldsByKey).forEach((field) => {
      const value = formMethods.watch(field.key);
      if ((field.type === 'list' || field.type === 'table') && !value) {
        setValue({ key: field.key, value: [] });
      }
    });
  }, [fieldsByKey, formMethods, setValue]);

  const showHeaderContent = currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId && !isMobile && !isCompleted;

  useEffect(() => {
    if (!showHeaderContent) {
      setShowFillablePdf(false);
      setShowExternalData(false);
    }
  }, [showHeaderContent]);

  useEffect(() => {
    if (showFillablePdf) {
      setShowExternalData(false);
    }
  }, [showFillablePdf]);

  useEffect(() => {
    if (showExternalData) {
      setShowFillablePdf(false);
    }
  }, [showExternalData]);

  const explanationModalToggle = ():void => {
    setExplanationModal(!explanationModal);
  };

  const showSideNav = !showFillablePdf && !showExternalData;

  return (
    <>
      {isEnabled && (
        <HandleWatchers
          formData={formData}
          onStartAgent={setFieldsAutoFilling}
        />
      )}
      {showHeaderContent && (
        <div className={s.headerContent}>
          <div className={s.buttons}>
            {!showSideNav && (
              <SectionNavigationButton
                sections={sectionsForNavigation}
                currentSection={currentSection}
                onSelectSection={setCurrentSection}
                completed={isCompleted}
              />
            )}
            <Button
              className={s.buttonCaseDoc}
              variant="secondary"
              size="sm"
              onClick={handleToggleShowFillablePdf}
            >
              {showFillablePdf
                ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
              <span className={s.toggleButtonLabel}>Show Fillable PDFs</span>
            </Button>
            {canShowExternalData && <Button
              className={s.buttonCaseDoc}
              variant="secondary"
              size="sm"
              onClick={handleToggleShowExternalData}
            >
              {showExternalData
                ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
              <span className={s.toggleButtonLabel}>Show External Data</span>
            </Button>}
          </div>
          <div className={s.buttons}>
            {showFillablePdf && pdfError && canUsePdfFillInsights && (
              <Button
                variant='secondary'
                size='sm'
                onClick={() => setIsPdfErrorModalOpen(true)}
              >
                Show PDF Error
              </Button>
            )}
            {showFillablePdf && pdfJson && canUsePdfFillInsights && (
              <Button
                variant='secondary'
                size='sm'
                onClick={() => setIsPdfJsonModalOpen(true)}
              >
                Show JSON
              </Button>
            )}
            {showFillablePdf && (
              <Button
                variant='primary'
                size='sm'
                loading={isFetchingPdfDocument}
                onClick={async () => {
                  if (!currentSectionData?.pdfTemplateId) {
                    return;
                  }
                  setIsFetchingPdfDocument(true);
                  const { data } = await generateFormPdfDocument({
                    pdfTemplateId: currentSectionData.pdfTemplateId,
                    questionnaireId: formData.id,
                    userWorkflowId,
                  });
                  if (data?.documentId) {
                    setPdfDocumentId(data.documentId);
                  }
                  if (data?.json) {
                    setPdfJson(data.json);
                  }
                  if (data?.error) {
                    setPdfError(data.error);
                  }
                  setIsFetchingPdfDocument(false);
                }}
              >
                Regenerate PDF
              </Button>
            )}
          </div>
        </div>
      )}
      {!showSideNav && isCompleted && <>
        <Spacer />
        <SectionNavigationButton
          sections={sectionsForNavigation}
          currentSection={currentSection}
          onSelectSection={setCurrentSection}
          completed={isCompleted}
        />
      </>}
      <div className={classNames(s.formContainer, isCompleted && s.completed)}
        {...!isEnabled
          ? {
            style: {
              alignItems: 'center',
              flexDirection: 'column',
            },
          }
          : {}
        }
      >
        {showSideNav && (
          <SectionNavigation
            sections={sectionsForNavigation}
            currentSection={currentSection}
            onSelectSection={setCurrentSection}
            completed={isCompleted}
            collapsed={!isEnabled}
            addStickyInset={Boolean(showHeaderContent)}
          />
        )}
        <div className={s.form}
          {...!isEnabled || (isCompleted && !showSideNav)
            ? {
              style: {
                borderLeft: 'none',
              },
            }
            : {}
          }
        >
          <div className={s.formContent}>
            {assignees.length > 0 && (
              <div className={s.filterContainer}>
                <button
                  className={selectedAssignee === 'All' ? s.activeFilter : ''}
                  onClick={() => setSelectedAssignee('All')}
                >
                  All
                </button>
                {assignees.map(assignee => (
                  <button
                    key={assignee.id}
                    className={
                      selectedAssignee === assignee.id ? s.activeFilter : ''
                    }
                    onClick={() => setSelectedAssignee(assignee.id)}
                  >
                    {assignee.label}
                  </button>
                ))}
              </div>
            )}
            {currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId && isCompleted && !isMobile && <>
              <Button
                className={classNames(s.buttonCaseDoc, s.completedShowPdf)}
                variant="secondary"
                size="sm"
                onClick={() => setShowFillablePdf(prev => !prev)}
              >
                {showFillablePdf
                  ? <ToggleRight width={20} height={20} weight='fill' color='var(--color-primary)' />
                  : <ToggleLeft width={20} height={20} weight='fill' color='var(--color-gray-50)' />}
                <span style={{ marginLeft: 8 }}>Show PDF</span>
              </Button>
              <Spacer size={24} />
            </>}
            <h2 className='heading-sm'>{currentSectionData?.label}</h2>
            {currentSectionData?.description && <p className="body-sm">{currentSectionData?.description}</p>}
            {(currentSectionData?.subsections?.length || 0) > 1 && (
              <div className={s.tabs}>
                {[
                  ...(currentSectionData?.subsections || []),
                  ...(currentSectionData?.fields ?? []).filter((f) => !(currentSectionData?.subsections || []).includes(f.subsection || '')).map((f) => f.subsection),
                ].filter(removeNullish).map((subsection) => {
                  const progress = {
                    completedFields: 0,
                    subsectionProgressPercentage: null,
                    totalFields: 0,
                  };
                  return (
                    <TabText
                      key={subsection}
                      icon={progress?.subsectionProgressPercentage === 100 ? CheckCircle : undefined}
                      iconColor='var(--color-success)'
                      label={`${subsection}${progress?.subsectionProgressPercentage && progress.subsectionProgressPercentage < 100
                        ? ` (${progress.completedFields}/${progress.totalFields})`
                        : ''}`}
                      selected={currentSubsection === subsection}
                      onClick={() => setCurrentSubsection(subsection === currentSubsection ? null : subsection)}
                    />
                  );
                })}
              </div>
            )}
            {currentSectionData?.isLoading && (
              <>
                <ProgressIndicator isCentered />
                <Spacer size={32} />
              </>
            )}
            {(currentSectionData?.fields ?? [])
              .filter((field) => !field.listFieldTemplateId && (currentSubsection
                ? field.subsection === currentSubsection || field?.listItemTemplates?.find((f) => f.subsection === currentSubsection) || field.type === 'median-income'
                : true))
              .map(field => (
                <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={field.key} className={s.field}>
                  <FormField
                    field={
                      field.type === 'list' || field.type === 'table'
                        ? ({
                          ...field,
                          ...(isCompleted && { disabled: true }),
                          fields: (currentSectionData?.fields ?? [])
                            .filter(({ listFieldTemplateId, subsection }) =>
                              listFieldTemplateId === field.id && (currentSubsection ? subsection === currentSubsection : true),
                            ).map(f => ({
                              ...f,
                              disabled: isCompleted,
                            })),
                          listItemTemplates: (currentSectionData?.fields ?? [])
                            .filter(({ listFieldTemplateId }) => listFieldTemplateId === field.id)
                            .map(f => ({
                              ...f,
                              disabled: isCompleted,
                            })),
                        } as Questionnaire.FieldList)
                        : {
                          ...field,
                          ...(isCompleted && { disabled: true }),
                        }
                    }
                    control={control}
                    isEnabled={isEnabled}
                    fieldsAutofilling={fieldsAutofilling}
                    rules={validationSchema[field.key]}
                    validationSchema={validationSchema}
                    onClickPopulatedFrom={(e: React.MouseEvent) => {
                      if(field?.type==='median-income') {
                        setFieldDataForExplanation(field);
                        setExplanationModal(true);
                      }
                      e.stopPropagation();
                      if (field.autofill?.expression?.variables[0]?.referenceType === 'data-reference-slug') {
                        setDataReferenceId({
                          dataReferenceId: field.autofill?.expression?.variables?.[0]?.referenceId,
                        });
                      }
                    }}
                  />
                </m.div>
              ))}
            {(isEnabled && !isCompleted) && (
              <m.div className={s.actions} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                key={currentSubsection || currentSection}>
                {isLastSection && (isLastSubsection || !currentSectionData?.subsections)
                  ? (
                    <SubmitButton
                      formData={formData}
                      shouldForceComplete={shouldForceComplete}
                    />
                  )
                  : (
                    <Button variant="primary" size="md" onClick={handleNextSection}>
                      Next
                      {(currentSubsection && currentSectionData?.subsections
                        && currentSectionData.subsections.length > currentSectionData.subsections.indexOf(currentSubsection) + 1)
                        ? `: ${currentSubsection && currentSectionData.subsections[currentSectionData.subsections.indexOf(currentSubsection) + 1]}`
                        : (visibleSections[nextSectionIndex]?.label && `: ${visibleSections[nextSectionIndex]?.label}` || '')
                      }
                    </Button>
                  )}
                <SaveButton
                  formData={formData}
                />
              </m.div>
            )}
          </div>
        </div>
        {showFillablePdf && currentSectionData?.pdfTemplateId && userWorkflowId && pdfDocumentId && (
          <div className={classNames(s.rightPanel, isCompleted && s.pdfContainerCompleted)}>
            <DocumentAccessProvider>
              <FormPDFViewer documentId={pdfDocumentId} />
            </DocumentAccessProvider>
          </div>
        )}
        {showExternalData && (
          <div className={s.rightPanel} style={{ backgroundColor: 'var(--color-gray-15)' }}>
            <ExternalData />
          </div>
        )}
      </div>
      {isPdfJsonModalOpen && pdfJson && <JsonModal json={pdfJson} onClose={() => setIsPdfJsonModalOpen(false)} />}
      {isPdfErrorModalOpen && pdfError && <JsonModal json={pdfError} onClose={() => setIsPdfErrorModalOpen(false)} />}
      {explanationModal
       && fieldDataForExplanation?.id
       && <AutofillExplanationModal
         formData={formData}
         fieldData={fieldDataForExplanation}
         toggleModal={explanationModalToggle}
       />
      }
    </>
  );
};

export default FormContent;
